import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { lowerCase } from "lodash";
import { useLocation } from "react-router";
import { FEATURE_FLAG_NAME } from "common/constants";
import { buildFullLink, buildLink, getPath } from "common/routing";
import { SUBMISSION_CATEGORY_TO_LABEL } from "common/utils/submissions";
import { getPublicPortalPrefix } from "common/utils/url";
import { colors } from "../../stitches.config";
import { track } from "../../utils/tracking";
import { AppProps } from "../App";
import { AuthContext } from "../Authorization/AuthContext";
import { AccountLogoOrFallbackIcon } from "../Common/AccountLogo";
import { Icon } from "../Common/Icons/LucideIcons";
import { Body } from "../Common/Typography";
import {
  AccountInfo,
  BottomNav,
  Container,
  ExpandedContent,
  Expander,
  HomeButton,
  ItemWrapper,
  Label,
  RecordsHoveredCategory,
  RecordsHoveredCategoryContainer,
  RecordsHoveredCategoryLink,
  StyledNavLink,
} from "./__styles__/SideNav";
import { AccountSwitcher } from "./AccountSwitcher";
import { reducer } from "./useSideNavReducer";

const LabelText = ({ children }: { children: ReactNode }) => {
  return (
    <Body size="default" type="regular" color="contentPrimaryDark" as="span">
      {children}
    </Body>
  );
};

type SideNavWidthContextType = {
  width: number;
};
export const SideNavWidthContext = createContext<SideNavWidthContextType>({
  width: 0,
});

const PublicAccountPortal = ({
  subdomain,
  expanded,
}: {
  subdomain: string;
  expanded: boolean;
}) => {
  const { account } = useContext(AuthContext);

  if (!account?.publicPortal.enabled) return null;

  const link = buildFullLink("guestHome", {
    prefix: getPublicPortalPrefix(subdomain),
    params: {},
  });

  return (
    <StyledNavLink
      as="a"
      href={link}
      data-testid="public-account-portal-link"
      target="_blank"
      onClick={() => track("Public website button clicked")}
    >
      <Label expanded={expanded}>
        <Icon color="contentPrimaryDark" iconName="panels-top-left" size="16" />
        <LabelText>Public site</LabelText>
      </Label>
    </StyledNavLink>
  );
};

const AccountSwitcherNavItem = ({ expanded }: { expanded: boolean }) => {
  const timeoutRef = useRef<Maybe<NodeJS.Timeout>>(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 50);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <StyledNavLink as="a">
        <Label
          aria-label="Account Links"
          expanded={expanded}
          hideLabelTextOnHover
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Icon
            color="contentPrimaryDark"
            iconName="arrow-right-square"
            size="16"
          />
          <LabelText>Account</LabelText>
        </Label>
      </StyledNavLink>
      <ExpandedContent
        expanded={expanded}
        isHovered={isHovered}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <AccountSwitcher />
      </ExpandedContent>
    </>
  );
};

const trackNavLinkClick = (location: string) => {
  track("Sidetab nav clicked", { location });
};

export type SideNavProps = AppProps & {
  setSideNavWidth?: (width: number) => void;
};

const SideNav = ({ account, setSideNavWidth }: SideNavProps) => {
  const { pathname } = useLocation();

  const [{ sideNavExpanded, recordsExpanded }, dispatch] = useReducer(reducer, {
    sideNavExpanded: false,
    recordsExpanded: false,
  });
  // This is only needed for updating aria-hidden for unit tests,
  // so not including in the reducer
  const [recordsHovered, setRecordsHovered] = useState(false);

  const sideNavRef = useRef<HTMLDivElement>(null);
  const { isFeatureEnabled } = useContext(AuthContext);

  useEffect(() => {
    if (sideNavRef.current && setSideNavWidth) {
      setSideNavWidth(sideNavRef.current.offsetWidth);
    }
  }, [sideNavExpanded, setSideNavWidth]);

  if (!account) {
    return null;
  }

  const categories = new Set(
    account.submissionTypes.map(type => type.category)
  );

  const allCategoryToData = [...categories].reduce(
    (acc, category) => {
      acc[category] = {
        url: buildLink("recordsTable", { category }),
        display: SUBMISSION_CATEGORY_TO_LABEL[category],
      };

      return acc;
    },
    {
      logs: { url: getPath("logs"), display: "Logs" },
    } as Record<string, { url: string; display: string }>
  );

  const onRecordTab = Object.values(allCategoryToData)
    .map(category => category.url)
    .includes(pathname);

  return (
    <Container ref={sideNavRef}>
      <AccountInfo>
        <HomeButton to={getPath("map")}>
          <AccountLogoOrFallbackIcon account={account} />
        </HomeButton>
        <ItemWrapper>
          <Expander onClick={() => dispatch({ toggle: "sideNavExpanded" })}>
            <Icon
              color="contentPrimaryDark"
              iconName={sideNavExpanded ? "chevron-left" : "chevron-right"}
              size="16"
              testId="side-nav-expander"
            />
          </Expander>
        </ItemWrapper>
      </AccountInfo>

      <StyledNavLink
        to={getPath("map")}
        onClick={() => trackNavLinkClick("map")}
      >
        <Label expanded={sideNavExpanded}>
          <Icon color="contentPrimaryDark" iconName="earth" size="16" />
          <LabelText>Map</LabelText>
        </Label>
      </StyledNavLink>
      {isFeatureEnabled(FEATURE_FLAG_NAME.PROPERTY_TABLE) && (
        <StyledNavLink
          to={getPath("properties")}
          onClick={() => trackNavLinkClick("properties")}
        >
          <Label expanded={sideNavExpanded}>
            <Icon color="contentPrimaryDark" iconName="home" size="16" />
            <LabelText>Properties</LabelText>
          </Label>
        </StyledNavLink>
      )}

      {Object.values(allCategoryToData).length > 0 && (
        <ItemWrapper
          expanded={recordsExpanded}
          data-testid="records-item-wrapper"
          // Because the icon isn't clickable when sidenav is collapsed,
          // we use default cursor to signify no click behavior
          style={{
            cursor: sideNavExpanded ? "pointer" : "default",
            backgroundColor:
              !recordsExpanded && onRecordTab
                ? colors.bgUiInteractionDark.value
                : "transparent",
          }}
        >
          <Label
            expanded={sideNavExpanded}
            onClick={() => {
              if (sideNavExpanded) {
                dispatch({ toggle: "recordsExpanded" });
              }
            }}
          >
            <Icon
              color="contentPrimaryDark"
              iconName="library"
              size="16"
              testId="records-icon"
              onMouseEnter={() => setRecordsHovered(true)}
              onMouseLeave={() => setRecordsHovered(false)}
            />
            {sideNavExpanded && <LabelText>Records</LabelText>}
            {sideNavExpanded && (
              <Icon
                color="contentPrimaryDark"
                iconName={recordsExpanded ? "chevron-up" : "chevron-down"}
                size="16"
              />
            )}
          </Label>

          {!sideNavExpanded && (
            <RecordsHoveredCategoryContainer
              role="menu"
              aria-hidden={!recordsHovered}
            >
              <RecordsHoveredCategory>
                <LabelText>Records</LabelText>
              </RecordsHoveredCategory>
              {Object.values(allCategoryToData).map((category, index) => (
                <RecordsHoveredCategoryLink
                  key={index}
                  to={category.url}
                  onClick={() => trackNavLinkClick(lowerCase(category.display))}
                  last={index === Object.values(allCategoryToData).length - 1}
                >
                  <LabelText>{category.display}</LabelText>
                </RecordsHoveredCategoryLink>
              ))}
            </RecordsHoveredCategoryContainer>
          )}

          {recordsExpanded && (
            <>
              {Object.values(allCategoryToData).map((category, index) => (
                <StyledNavLink
                  key={index}
                  to={category.url}
                  onClick={() => trackNavLinkClick(lowerCase(category.display))}
                >
                  <Label expanded style={{ paddingLeft: "40px" }}>
                    <LabelText>{category.display}</LabelText>
                  </Label>
                </StyledNavLink>
              ))}
            </>
          )}
        </ItemWrapper>
      )}

      <StyledNavLink
        to={getPath("documentUploads")}
        onClick={() => trackNavLinkClick("files")}
      >
        <Label expanded={sideNavExpanded}>
          <Icon color="contentPrimaryDark" iconName="file-text" size="16" />
          <LabelText>Files</LabelText>
        </Label>
      </StyledNavLink>
      <StyledNavLink
        to={getPath("settings")}
        onClick={() => trackNavLinkClick("settings")}
      >
        <Label expanded={sideNavExpanded}>
          <Icon color="contentPrimaryDark" iconName="settings" size="16" />
          <LabelText>Settings</LabelText>
        </Label>
      </StyledNavLink>

      <BottomNav>
        <PublicAccountPortal
          subdomain={account.subdomain}
          expanded={sideNavExpanded}
        />
        <AccountSwitcherNavItem expanded={sideNavExpanded} />
      </BottomNav>
    </Container>
  );
};
export default SideNav;
